import { useState } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { format } from 'date-fns';
import { CreateHealthTestDto, COMMON_HEALTH_TESTS, COMMON_TEST_RESULTS } from '../../api/health-tests';
import { Bird } from '../../api/birds';
import {
  BrandModal,
  BrandModalBody,
  BrandModalFooter,
  BrandModalHeader,
} from '../ui/BrandModal';
import AmountInput from '../ui/AmountInput';

interface AddHealthTestModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: CreateHealthTestDto) => Promise<void>;
  birds?: Bird[];
}

const healthTestSchema = z.object({
  testName: z.string().min(1, 'Test name is required'),
  testeeName: z.string().min(1, 'Testee name is required'),
  testDate: z.string().min(1, 'Test date is required'),
  result: z.string().optional(),
  comment: z.string().optional(),
  description: z.string().optional(),
  cost: z.number().min(0, 'Cost must be positive'),
  notes: z.string().optional(),
  birdId: z.string().min(1, 'Bird selection is required'),
});

type HealthTestFormData = z.infer<typeof healthTestSchema>;

export default function AddHealthTestModal({
  isOpen,
  onClose,
  onSubmit,
  birds,
}: AddHealthTestModalProps) {
  const [isSubmitting, setIsSubmitting] = useState(false);

  const {
    register,
    handleSubmit,
    reset,
    control,
    formState: { errors },
  } = useForm<HealthTestFormData>({
    resolver: zodResolver(healthTestSchema),
    defaultValues: {
      testDate: format(new Date(), 'yyyy-MM-dd'),
      cost: 0,
    },
  });

  const handleFormSubmit = async (data: HealthTestFormData) => {
    try {
      setIsSubmitting(true);
      await onSubmit(data as CreateHealthTestDto);
      reset();
      onClose();
    } catch (error) {
      console.error('Error submitting health test:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  return (
    <BrandModal open={isOpen} onClose={handleClose} maxWidthClass="max-w-2xl">
      <BrandModalHeader
        title="Add Health Test"
        subtitle="Record a new health test for one of your birds."
        onClose={handleClose}
      />
      <form onSubmit={handleSubmit(handleFormSubmit)}>
        <BrandModalBody className="space-y-6">
                      <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
                        {/* Bird Selection */}
                        <div className="sm:col-span-2">
                          <label htmlFor="birdId" className="block text-sm font-medium text-slate-700">
                            Bird *
                          </label>
                          <select
                            id="birdId"
                            {...register('birdId')}
                            className="mt-1 block w-full input border-gray-300 shadow-sm sm:text-sm"
                          >
                            <option key="empty-option-1753461297318" value="">Select a bird</option>
                            <option key="ALL_BIRDS-1753461297318" value="ALL_BIRDS" className="font-medium text-cyan-600">
                              🔵 All Birds / Flock
                            </option>
                            {Array.isArray(birds) && birds.map((bird) => (
                              <option key={bird.id} value={bird.id}>
                                {bird.bandId} - {bird.species?.commonName || 'Unknown Species'}
                              </option>
                            ))}
                          </select>
                          {errors.birdId && (
                            <p className="mt-1 text-sm text-red-600">{errors.birdId.message}</p>
                          )}
                        </div>

                        {/* Test Name */}
                        <div>
                          <label htmlFor="testName" className="block text-sm font-medium text-slate-700">
                            Test Name *
                          </label>
                          <input
                            type="text"
                            id="testName"
                            list="testNameOptions"
                            {...register('testName')}
                            className="mt-1 block w-full input border-gray-300 shadow-sm sm:text-sm"
                            placeholder="Enter test name"
                          />
                          <datalist id="testNameOptions">
                            {COMMON_HEALTH_TESTS.map((test) => (
                              <option key={test} value={test} />
                            ))}
                          </datalist>
                          {errors.testName && (
                            <p className="mt-1 text-sm text-red-600">{errors.testName.message}</p>
                          )}
                        </div>

                        {/* Testee Name */}
                        <div>
                          <label htmlFor="testeeName" className="block text-sm font-medium text-slate-700">
                            Tested By *
                          </label>
                          <input
                            type="text"
                            id="testeeName"
                            {...register('testeeName')}
                            className="mt-1 block w-full input border-gray-300 shadow-sm sm:text-sm"
                            placeholder="Veterinarian or lab name"
                          />
                          {errors.testeeName && (
                            <p className="mt-1 text-sm text-red-600">{errors.testeeName.message}</p>
                          )}
                        </div>

                        {/* Test Date */}
                        <div>
                          <label htmlFor="testDate" className="block text-sm font-medium text-slate-700">
                            Test Date *
                          </label>
                          <input
                            type="date"
                            id="testDate"
                            {...register('testDate')}
                            className="mt-1 block w-full input border-gray-300 shadow-sm sm:text-sm"
                          />
                          {errors.testDate && (
                            <p className="mt-1 text-sm text-red-600">{errors.testDate.message}</p>
                          )}
                        </div>

                        {/* Cost */}
                        <div>
                          <label htmlFor="cost" className="block text-sm font-medium text-slate-700">
                            Cost (R) *
                          </label>
                          <Controller
                            name="cost"
                            control={control}
                            render={({ field }) => (
                              <AmountInput
                                id="cost"
                                value={field.value}
                                onChange={field.onChange}
                                required
                                min={0}
                              />
                            )}
                          />
                          {errors.cost && (
                            <p className="mt-1 text-sm text-red-600">{errors.cost.message}</p>
                          )}
                        </div>

                        {/* Result */}
                        <div className="sm:col-span-2">
                          <label htmlFor="result" className="block text-sm font-medium text-slate-700">
                            Result
                          </label>
                          <input
                            type="text"
                            id="result"
                            list="resultOptions"
                            {...register('result')}
                            className="mt-1 block w-full input border-gray-300 shadow-sm sm:text-sm"
                            placeholder="Test result (optional)"
                          />
                          <datalist id="resultOptions">
                            {COMMON_TEST_RESULTS.map((result) => (
                              <option key={result} value={result} />
                            ))}
                          </datalist>
                          {errors.result && (
                            <p className="mt-1 text-sm text-red-600">{errors.result.message}</p>
                          )}
                        </div>

                        {/* Description */}
                        <div className="sm:col-span-2">
                          <label htmlFor="description" className="block text-sm font-medium text-slate-700">
                            Description
                          </label>
                          <textarea
                            id="description"
                            rows={3}
                            {...register('description')}
                            className="mt-1 block w-full input border-gray-300 shadow-sm sm:text-sm"
                            placeholder="Test description or purpose"
                          />
                          {errors.description && (
                            <p className="mt-1 text-sm text-red-600">{errors.description.message}</p>
                          )}
                        </div>

                        {/* Comment */}
                        <div className="sm:col-span-2">
                          <label htmlFor="comment" className="block text-sm font-medium text-slate-700">
                            Comment
                          </label>
                          <textarea
                            id="comment"
                            rows={3}
                            {...register('comment')}
                            className="mt-1 block w-full input border-gray-300 shadow-sm sm:text-sm"
                            placeholder="Additional comments about the test"
                          />
                          {errors.comment && (
                            <p className="mt-1 text-sm text-red-600">{errors.comment.message}</p>
                          )}
                        </div>

                        {/* Notes */}
                        <div className="sm:col-span-2">
                          <label htmlFor="notes" className="block text-sm font-medium text-slate-700">
                            Notes
                          </label>
                          <textarea
                            id="notes"
                            rows={3}
                            {...register('notes')}
                            className="mt-1 block w-full input border-gray-300 shadow-sm sm:text-sm"
                            placeholder="Internal notes"
                          />
                          {errors.notes && (
                            <p className="mt-1 text-sm text-red-600">{errors.notes.message}</p>
                          )}
                        </div>
                      </div>

        </BrandModalBody>
        <BrandModalFooter>
          <button
            type="button"
            className="btn btn-secondary text-sm"
            onClick={handleClose}
            disabled={isSubmitting}
          >
            Cancel
          </button>
          <button
            type="submit"
            className="btn btn-primary text-sm disabled:opacity-50"
            disabled={isSubmitting}
          >
            {isSubmitting ? 'Adding...' : 'Add Health Test'}
          </button>
        </BrandModalFooter>
      </form>
    </BrandModal>
  );
} 